import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  Activity,
  AlertTriangle,
  ArrowRight,
  Calendar,
  ClipboardList,
  Pill,
  Shield,
  ShieldCheck,
  X,
} from 'lucide-react';
import { Patient } from '@/lib/types';
import { useClinic } from '@/contexts/ClinicContext';
import {
  vitalsService,
  VitalLoinc,
  readVitalNumber,
  type VitalObservation,
} from '@/services/vitalsService';
import { inchesToFeetInches } from '@/lib/vitals/parseHeight';
import { allergiesService } from '@/services/allergiesService';
import { problemsService } from '@/services/problemsService';
import { medicationsService } from '@/services/medicationsService';
import {
  Sheet,
  SheetContent,
  SheetClose,
  SheetTitle,
} from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import PatientUpcomingVisitsPanel from './PatientUpcomingVisitsPanel';

interface PatientSummaryContentProps {
  patient: Patient | null;
}

/**
 * Practitioner-focused Summary surface. Glance-only — every section is
 * read-only; deep edits live in the Chart / Orders & Rx tabs. The goal is
 * "what do I need to know about this patient before I do anything?" in a
 * single screen.
 *
 * Replaces the legacy 1300-line PatientOverviewTab on this surface.
 * Demographics moved to the page header. Patient Portal moved to a header
 * pill. Audit trail / Recent activity moved to the Timeline tab in
 * secondary nav.
 */
export default function PatientSummaryContent({
  patient,
}: PatientSummaryContentProps) {
  const navigate = useNavigate();
  const { clinic } = useClinic();
  const clinicId = clinic?.id || '';
  const patientId = patient?.id || '';
  const enabled = !!clinicId && !!patientId;
  const [vitalsDrawerOpen, setVitalsDrawerOpen] = useState(false);

  const { data: latestVitals } = useQuery({
    queryKey: ['summary', patientId, 'vitals-latest'],
    queryFn: () => vitalsService.getLatestVitals(clinicId, patientId),
    enabled,
  });

  const { data: allergyNames = [] } = useQuery<string[]>({
    queryKey: ['summary', patientId, 'allergies-active-med-names'],
    queryFn: () =>
      allergiesService.getActiveMedicationAllergyNames(clinicId, patientId),
    enabled,
  });

  const { data: conditions = [] } = useQuery({
    queryKey: ['summary', patientId, 'problems-active'],
    queryFn: () =>
      problemsService.getPatientConditions(clinicId, patientId, {
        activeOnly: true,
      }),
    enabled,
  });

  const { data: meds = [] } = useQuery({
    queryKey: ['summary', patientId, 'medications-active'],
    queryFn: () =>
      medicationsService.getPatientMedications(clinicId, patientId, {
        activeOnly: true,
      }),
    enabled,
  });

  const goChart = (anchor?: string) => {
    if (!patientId) return;
    const url = `/patients/${patientId}?tab=chart${anchor ? `&anchor=${anchor}` : ''}`;
    navigate(url);
  };
  const goOrders = () => {
    if (!patientId) return;
    navigate(`/patients/${patientId}?tab=orders-rx`);
  };

  const vitalsMap: Record<string, VitalObservation | null> = latestVitals ?? {};
  const weight = readVitalNumber(vitalsMap[VitalLoinc.BODY_WEIGHT]);
  const bpSys = readVitalNumber(vitalsMap[VitalLoinc.SYSTOLIC_BP]);
  const bpDia = readVitalNumber(vitalsMap[VitalLoinc.DIASTOLIC_BP]);
  const lastVitalsAt =
    vitalsMap[VitalLoinc.BODY_WEIGHT]?.effective_date_time
    || vitalsMap[VitalLoinc.SYSTOLIC_BP]?.effective_date_time
    || null;

  return (
    <>
    <div className='space-y-4'>
      {/* Allergies + Problems strip — safety-first row */}
      <div className='grid grid-cols-1 gap-3 lg:grid-cols-2'>
        <SafetyChipRow
          icon={Shield}
          label='Active Allergies'
          accent='red'
          empty='No active medication allergies'
          items={allergyNames}
          onSeeAll={() => goChart('allergies')}
        />
        <SafetyChipRow
          icon={ClipboardList}
          label='Active Problems'
          accent='amber'
          empty='No active problems'
          items={conditions
            .map((c) => c.display || c.code || '')
            .filter(Boolean)
            .slice(0, 6)}
          onSeeAll={() => goChart('problems')}
        />
      </div>

      {/* Vitals + Active Meds — fit a 2-column row at md+ (each ~400-500px,
          plenty for compact content). Upcoming visits is its own full-width
          row below so the appointment card has room for the date, provider
          name, and action button without wrapping. */}
      <div className='grid grid-cols-1 gap-4 md:grid-cols-2'>
        {/* Vitals snapshot */}
        <SummaryCard
          icon={Activity}
          title='Latest Vitals'
          onSeeAll={() => setVitalsDrawerOpen(true)}
          seeAllLabel='View all'
        >
          <div className='space-y-2'>
            <VitalRow
              label='Blood Pressure'
              value={
                bpSys != null && bpDia != null
                  ? `${bpSys}/${bpDia} mmHg`
                  : null
              }
            />
            <VitalRow
              label='Weight'
              value={weight != null ? `${weight} lb` : null}
            />
            {lastVitalsAt && (
              <p className='pt-1 text-[11px] text-slate-400'>
                Last recorded {formatRelative(lastVitalsAt)}
              </p>
            )}
            {!bpSys && !weight && (
              <p className='text-xs text-slate-500'>No vitals on file yet.</p>
            )}
          </div>
        </SummaryCard>

        {/* Active medications */}
        <SummaryCard
          icon={Pill}
          title='Active Medications'
          onSeeAll={goOrders}
          seeAllLabel='Orders & Rx'
        >
          {meds.length === 0 ? (
            <p className='text-xs text-slate-500'>No active medications.</p>
          ) : (
            <ul className='space-y-1.5'>
              {meds.slice(0, 5).map((m) => {
                const dose = formatDoseLabel(m.dose_text);
                return (
                  <li
                    key={m.id}
                    className='flex min-w-0 items-center justify-between gap-2 overflow-hidden rounded-md bg-slate-50 px-2.5 py-1.5'
                    title={m.medication_display}
                  >
                    <div className='min-w-0 flex-1 truncate text-sm text-slate-800'>
                      {m.medication_display}
                    </div>
                    {dose && (
                      <div className='flex-shrink-0 text-[11px] text-slate-500'>
                        {dose}
                      </div>
                    )}
                  </li>
                );
              })}
              {meds.length > 5 && (
                <li className='pt-1 text-[11px] text-slate-500'>
                  +{meds.length - 5} more
                </li>
              )}
            </ul>
          )}
        </SummaryCard>
      </div>

      {/* Upcoming visits — full width so the date / provider / action button
          render comfortably without word-wrapping in narrow columns. The
          existing panel has its own card chrome, so we render it bare and
          give it the full content area. */}
      <SummaryCard icon={Calendar} title='Upcoming Visits' bare>
        <div className='p-4'>
          <PatientUpcomingVisitsPanel patientId={patientId} embedded />
        </div>
      </SummaryCard>

      {/* Insurance — administrative billing info, surfaced here since
          PatientOverviewTab was retired in the IA consolidation. */}
      <SummaryCard icon={ShieldCheck} title='Insurance'>
        {patient?.insurance_carrier
        || patient?.insurance_member_id
        || patient?.insurance_group_id ? (
            <div className='grid grid-cols-1 gap-3 md:grid-cols-3'>
              <div>
                <p className='text-[11px] font-medium uppercase tracking-wide text-slate-500'>
                  Carrier
                </p>
                <p className='break-words text-sm text-slate-900'>
                  {patient.insurance_carrier || '—'}
                </p>
              </div>
              <div>
                <p className='text-[11px] font-medium uppercase tracking-wide text-slate-500'>
                  Member ID
                </p>
                <p className='break-words text-sm text-slate-900'>
                  {patient.insurance_member_id || '—'}
                </p>
              </div>
              <div>
                <p className='text-[11px] font-medium uppercase tracking-wide text-slate-500'>
                  Group ID
                </p>
                <p className='break-words text-sm text-slate-900'>
                  {patient.insurance_group_id || '—'}
                </p>
              </div>
            </div>
          ) : (
            <p className='text-xs text-slate-500'>
              No insurance information on file.
            </p>
          )}
      </SummaryCard>
    </div>

    <LatestVitalsDrawer
      open={vitalsDrawerOpen}
      onOpenChange={setVitalsDrawerOpen}
      vitalsMap={vitalsMap}
      onSeeFullHistory={() => {
        setVitalsDrawerOpen(false);
        goChart('vitals');
      }}
    />
    </>
  );
}

// ─── Subcomponents ───────────────────────────────────────────────────────────

function SummaryCard({
  icon: Icon,
  title,
  onSeeAll,
  seeAllLabel,
  bare,
  children,
}: {
  icon: React.ElementType;
  title: string;
  onSeeAll?: () => void;
  seeAllLabel?: string;
  bare?: boolean;
  children: React.ReactNode;
}) {
  return (
    <section className='min-w-0 overflow-hidden rounded-lg border border-slate-200 bg-white shadow-sm'>
      <header className='flex items-center justify-between gap-2 border-b border-slate-100 px-4 py-2.5'>
        <div className='flex min-w-0 items-center gap-2'>
          <Icon className='h-4 w-4 flex-shrink-0 text-slate-500' />
          <h3 className='truncate text-sm font-semibold text-slate-900'>
            {title}
          </h3>
        </div>
        {onSeeAll && (
          <button
            type='button'
            onClick={onSeeAll}
            className='inline-flex flex-shrink-0 items-center gap-1 text-[11px] font-medium text-teal-700 hover:text-teal-800'
          >
            {seeAllLabel || 'View'}
            <ArrowRight className='h-3 w-3' />
          </button>
        )}
      </header>
      <div className={bare ? 'min-w-0 p-0' : 'min-w-0 px-4 py-3'}>
        {children}
      </div>
    </section>
  );
}

function VitalRow({ label, value }: { label: string; value: string | null }) {
  return (
    <div className='flex items-baseline justify-between gap-2'>
      <span className='text-xs text-slate-500'>{label}</span>
      <span className='text-sm font-medium text-slate-900'>
        {value ?? <span className='text-slate-400'>—</span>}
      </span>
    </div>
  );
}

function SafetyChipRow({
  icon: Icon,
  label,
  accent,
  items,
  empty,
  onSeeAll,
}: {
  icon: React.ElementType;
  label: string;
  accent: 'red' | 'amber';
  items: string[];
  empty: string;
  onSeeAll?: () => void;
}) {
  const tone =
    accent === 'red'
      ? 'border-red-200 bg-red-50/60 text-red-700'
      : 'border-amber-200 bg-amber-50/60 text-amber-800';
  const chipTone =
    accent === 'red'
      ? 'bg-red-100 text-red-800 border-red-200'
      : 'bg-amber-100 text-amber-800 border-amber-200';

  return (
    <div
      className={`flex flex-wrap items-center gap-2 rounded-lg border px-3 py-2.5 ${tone}`}
    >
      <span className='flex flex-shrink-0 items-center gap-1.5 text-xs font-semibold uppercase tracking-wide'>
        {accent === 'red' ? (
          <AlertTriangle className='h-3.5 w-3.5' />
        ) : (
          <Icon className='h-3.5 w-3.5' />
        )}
        {label}
      </span>
      {items.length === 0 ? (
        <span className='text-xs italic opacity-80'>{empty}</span>
      ) : (
        <>
          {items.map((it, i) => (
            <span
              key={i}
              className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[11px] font-medium ${chipTone}`}
            >
              {it}
            </span>
          ))}
        </>
      )}
      {onSeeAll && items.length > 0 && (
        <button
          type='button'
          onClick={onSeeAll}
          className='ml-auto inline-flex items-center gap-1 text-[11px] font-medium opacity-80 hover:opacity-100'
        >
          See all <ArrowRight className='h-3 w-3' />
        </button>
      )}
    </div>
  );
}

function formatDoseLabel(doseText?: string | null): string | null {
  if (!doseText) return null;
  const trimmed = doseText.trim();
  if (!trimmed) return null;
  // If the dose is purely numeric (e.g. "1") it's ambiguous without a unit
  // — hide it on this glance surface; the full Orders & Rx tab shows the
  // structured dose / frequency.
  if (/^\d+(\.\d+)?$/.test(trimmed)) return null;
  // dose_text often arrives as "<strength> — <sig>" (e.g.
  // "100mg/2.5mg/mL — Inject 0.1 mL subcutaneously…"). On this glance row
  // we want just the strength — the full sig blows out the layout and
  // lives on the Orders & Rx tab anyway.
  const strength = trimmed.split(/\s+[—–-]\s+/)[0].trim();
  return strength || trimmed;
}

function formatRelative(iso: string): string {
  const ts = new Date(iso).getTime();
  if (!Number.isFinite(ts)) return '';
  const diff = Date.now() - ts;
  const minutes = Math.floor(diff / 60_000);
  if (minutes < 1) return 'just now';
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
}

// ─── Latest Vitals Drawer ────────────────────────────────────────────────────

interface VitalRowSpec {
  label: string;
  loinc: string;
  unit?: string;
  /**
   * When set, replaces both the value and unit rendering with this
   * pre-formatted string. Used for rows that need cross-LOINC composition
   * (BP) or unit conversion (height → ft+in).
   */
  format?: (
    vitalsMap: Record<string, VitalObservation | null>,
  ) => { value: string; unit?: string } | null;
}

const formatHeight = (
  vitalsMap: Record<string, VitalObservation | null>,
): { value: string; unit?: string } | null => {
  const raw = readVitalNumber(vitalsMap[VitalLoinc.BODY_HEIGHT]);
  if (raw == null) return null;
  // Height is always stored in inches regardless of the unit label the
  // observation came back with — ignore `value_quantity_unit` and convert
  // straight to ft + in.
  const fi = inchesToFeetInches(raw);
  if (!fi) return null;
  const inchPart = Number.isInteger(fi.in) ? fi.in : fi.in.toFixed(1);
  return { value: `${fi.ft}′ ${inchPart}″` };
};

const VITAL_ROWS: VitalRowSpec[] = [
  {
    label: 'Blood pressure',
    loinc: VitalLoinc.SYSTOLIC_BP,
    format: (m) => {
      const s = readVitalNumber(m[VitalLoinc.SYSTOLIC_BP]);
      const d = readVitalNumber(m[VitalLoinc.DIASTOLIC_BP]);
      if (s == null || d == null) return null;
      return { value: `${s}/${d}`, unit: 'mmHg' };
    },
  },
  { label: 'Heart rate', loinc: VitalLoinc.HEART_RATE, unit: 'bpm' },
  { label: 'Respiratory rate', loinc: VitalLoinc.RESPIRATORY_RATE, unit: '/min' },
  { label: 'Temperature', loinc: VitalLoinc.BODY_TEMPERATURE, unit: '°F' },
  { label: 'Oxygen saturation', loinc: VitalLoinc.OXYGEN_SATURATION, unit: '%' },
  { label: 'Weight', loinc: VitalLoinc.BODY_WEIGHT, unit: 'lb' },
  { label: 'Height', loinc: VitalLoinc.BODY_HEIGHT, format: formatHeight },
  { label: 'BMI', loinc: VitalLoinc.BMI, unit: 'kg/m²' },
  { label: 'Pain scale', loinc: VitalLoinc.PAIN_SCALE, unit: '/10' },
];

function formatVitalsTimestamp(iso?: string | null): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

function LatestVitalsDrawer({
  open,
  onOpenChange,
  vitalsMap,
  onSeeFullHistory,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  vitalsMap: Record<string, VitalObservation | null>;
  onSeeFullHistory: () => void;
}) {
  const rows = VITAL_ROWS.map((spec) => {
    const obs = vitalsMap[spec.loinc];
    const formatted = spec.format ? spec.format(vitalsMap) : null;
    const value = formatted
      ? formatted.value
      : (() => {
          const n = readVitalNumber(obs);
          return n == null ? null : String(n);
        })();
    const unit = formatted
      ? formatted.unit
      : obs?.value_quantity_unit || spec.unit;
    return {
      label: spec.label,
      value,
      unit,
      recordedAt: obs?.effective_date_time ?? null,
      interpretation: obs?.interpretation_code ?? null,
    };
  });
  const haveAny = rows.some((r) => r.value != null);

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className='w-full !max-w-[480px] p-0 flex flex-col gap-0 [&>button]:hidden'>
        <div className='flex items-center justify-between border-b border-slate-200 px-6 py-4'>
          <div className='flex items-center gap-2'>
            <Activity className='h-5 w-5 text-violet-500' />
            <SheetTitle
              className='font-semibold text-slate-900'
              style={{ fontSize: '1.05rem' }}
            >
              Latest Vitals
            </SheetTitle>
          </div>
          <SheetClose asChild>
            <button
              aria-label='Close'
              className='flex h-10 w-10 items-center justify-center rounded-full border border-slate-200 hover:bg-slate-50'
            >
              <X className='h-5 w-5 text-slate-600' />
            </button>
          </SheetClose>
        </div>

        <div className='flex-1 overflow-y-auto px-6 py-5'>
          {!haveAny ? (
            <p className='text-sm text-slate-500'>
              No vitals recorded for this patient yet.
            </p>
          ) : (
            <ul className='divide-y divide-slate-100'>
              {rows.map((row) => (
                <li
                  key={row.label}
                  className='flex items-start justify-between gap-3 py-3'
                >
                  <div className='min-w-0'>
                    <div className='text-sm font-medium text-slate-700'>
                      {row.label}
                    </div>
                    {row.recordedAt && row.value != null && (
                      <div className='text-[11px] text-slate-400'>
                        Recorded {formatVitalsTimestamp(row.recordedAt)}
                      </div>
                    )}
                  </div>
                  <div className='flex-shrink-0 text-right'>
                    {row.value != null ? (
                      <span className='text-sm font-semibold text-slate-900'>
                        {row.value}
                        {row.unit && (
                          <span className='ml-1 text-xs font-normal text-slate-500'>
                            {row.unit}
                          </span>
                        )}
                      </span>
                    ) : (
                      <span className='text-sm text-slate-400'>—</span>
                    )}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className='flex items-center justify-end border-t border-slate-200 px-6 py-4'>
          <Button variant='outline' size='sm' onClick={onSeeFullHistory}>
            See full history
          </Button>
        </div>
      </SheetContent>
    </Sheet>
  );
}
